import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import {
  Close as CloseIcon,
  Visibility as ViewIcon,
  VisibilityOff as OffIcon,
} from "@material-ui/icons";
import {
  passwordValidation,
  passwordValidationLabel,
} from "../Settings/AccountSettings/password-validaton";
import InfoIcon from "@material-ui/icons/Info";

const useStyles = makeStyles((theme) => ({
  submitButton: {
    backgroundColor: "#1565C0",
    color: "#ffffff",
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
    padding: "12px",
    "&:hover": {
      backgroundColor: "#0d47a1",
    },
  },
  backButton: {
    color: "#1565C0",
    textTransform: "none",
    padding: 0,
    fontSize: "1rem",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
}));


const ResetPasswordFormikComponent = (props) => {
  const { type, classes, showTitle, title } = props;
  const formClasses = useStyles();
  const [open, setOpen] = React.useState(true);
  const [resendCooldown, setResendCooldown] = React.useState(0); // 0 until a code is actually sent
  const [codeExpired, setCodeExpired] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const resetFormRef = React.useRef(null);

  // Derived directly from props so it takes effect on the same render the error arrives
  // Uses the codeExpired flag from the backend response (via Redux) — not a message string match
  const isCodeExpired = codeExpired || props.resetCodeExpired === true;

  // Countdown timer — only ticks when cooldown > 0
  React.useEffect(() => {
    if (type !== "resetpassword" || resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [type, resendCooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start/restart timer when a code is successfully sent
  React.useEffect(() => {
    if (type !== "resetpassword" || !props.forgotPasswordSuccess) return;
    setResendLoading(false);
    setResendCooldown(60);
    setCodeExpired(false);
    if (resetFormRef.current) resetFormRef.current();
  }, [props.alert?.id, type]); // eslint-disable-line react-hooks/exhaustive-deps

  // On any error: clear loading, set correct timer duration
  React.useEffect(() => {
    if (type !== "resetpassword" || !props.errorMessage) return;
    setResendLoading(false);
    if (props.resendLockoutSeconds > 0) {
      setResendCooldown(props.resendLockoutSeconds);
    } else {
      setResendCooldown(60);
    }
    if (props.resetCodeExpired === true) {
      setCodeExpired(true);
    }
  }, [props.alert?.id, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCooldown = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
   <Grid container>
     {showTitle && (
       <Grid item xs={12} style={{ marginBottom: 24 }}>
         <Typography align="center" variant="h2" style={{ fontWeight: 600 }}>
           {title || (type === 'changepassword' ? 'Change Password' : type === 'resetpassword' ? 'Reset Password' : 'Set Password')}
         </Typography>
       </Grid>
     )}
     <Grid item xs={12}>
     <Formik
      initialValues={{
        oldpassword: "",
        code: "",
        password: "",
        confirm: "",
        visible1: false,
        visible2: false,
        visible3: false,
      }}
      validationSchema={Yup.object().shape({
        oldpassword:
          type === "changepassword"
            ? Yup.string().max(255).required("Current Password is required.")
            : Yup.string(),
        code:
          type === "resetpassword"
            ? Yup.number()
                .integer()
                .typeError("Code is a 6 digit integer.")
                .required("Code is required.")
            : Yup.number(),
        password: passwordValidation,
        confirm: Yup.string().oneOf(
          [Yup.ref("password"), null],
          "Passwords must match."
        ),
      })}
      onSubmit={(values, { resetForm }) => {
        setOpen(false);
        if (type === "changepassword") {
          props.changePass(values.oldpassword, values.password);
        } else if (type === "resetpassword") {
          if (isCodeExpired) return;
          props.changePass(
            values.code,
            values.password,
            props.forgotPassToken,
            props.forgotPassEmail
          );
          props.setState({
            open: false,
          });
        } else {
          props.changePass(values.password);
        }
        resetForm({});
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        resetForm,
      }) => {
        resetFormRef.current = resetForm;
        return (
        <form noValidate onSubmit={handleSubmit} className={classes.formStyle}>

       {props.type === "resetpassword" &&   <Collapse in={open}>
            <Alert
              severity="info"
              variant="filled"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setOpen(false)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <Typography variant="body1">{"If the email you provided is associated with an active SkyGeni account, you will receive a code for resetting your password."}</Typography>
            </Alert>
          </Collapse>}

          {props.type === "changepasswordfirstime" && (
            <Typography variant="body1">
              Welcome to the SkyGeni platform. Please change your password to
              proceed.
            </Typography>
          )}

          {props.errorMessage !== "" && (
            <Box mt={5}>
              <Collapse in={props.open} data-test="reset-pass">
                <Alert
                  severity="error"
                  color={props.alert.type === "success" ? "success" : "error"}
                  variant="filled"
                  action={
                    props.alert.type === "success" ? (
                      <></>
                    ) : (
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => {
                          props.setState({
                            open: false,
                            errorMessage: "",
                          });
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    )
                  }
                >
                  <Typography data-test="reset-pass-error-text" variant="body1">
                    {props.errorMessage}
                  </Typography>
                </Alert>
              </Collapse>
            </Box>
          )}

          {type === "changepassword" && (
            <Box mb={2}>
              <Typography variant="body2" style={{ marginBottom: "8px", color: "#424242", fontSize: "0.875rem" }}>
                Current Password
              </Typography>
              <TextField
                error={Boolean(touched.oldpassword && errors.oldpassword)}
                fullWidth
                helperText={touched.oldpassword && errors.oldpassword}
                placeholder="Enter current password"
                name="oldpassword"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.oldpassword}
                variant="outlined"
                type={values.visible1 ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setFieldValue("visible1", !values.visible1, false)
                        }
                      >
                        {values.visible1 ? <OffIcon /> : <ViewIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {props.changePasswordSuccess !== true && !isCodeExpired && (
            <>
              {type === "resetpassword" && (
                <Box mb={2} mt={3}>
                  <Typography variant="body2" style={{ marginBottom: "8px", color: "#424242", fontSize: "0.875rem" }}>
                    Code
                  </Typography>
                  <TextField
                    error={Boolean(touched.code && errors.code)}
                    fullWidth
                    helperText={touched.code && errors.code}
                    placeholder="Enter code"
                    name="code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.code}
                    variant="outlined"
                  />
                </Box>
              )}
              <Box mb={2}>
                <Typography variant="body2" style={{ marginBottom: "8px", color: "#424242", fontSize: "0.875rem" }}>
                  New Password
                </Typography>
                <TextField
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  placeholder="Enter new password"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  variant="outlined"
                  type={values.visible2 ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setFieldValue("visible2", !values.visible2, false)
                          }
                        >
                          {values.visible2 ? <OffIcon /> : <ViewIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Grid
                container
                direction="column"
                spacing={1}
                style={{ marginTop: 5 }}
              >
                {passwordValidationLabel.map((item, index) => (
                  <Grid item key={index}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <InfoIcon style={{ fontSize: 16 }} />
                      </Grid>
                      <Grid item>
                        <Typography variant="body2">{item}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Box mb={2}>
                <Typography variant="body2" style={{ marginBottom: "8px", color: "#424242", fontSize: "0.875rem" }}>
                  Confirm New Password
                </Typography>
                <TextField
                  error={Boolean(touched.confirm && errors.confirm)}
                  fullWidth
                  helperText={touched.confirm && errors.confirm}
                  placeholder="Confirm new password"
                  name="confirm"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirm}
                  variant="outlined"
                  type={values.visible3 ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setFieldValue("visible3", !values.visible3, false)
                          }
                        >
                          {values.visible3 ? <OffIcon /> : <ViewIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </>
          )}

          <Box mt={3}>
            <Grid item container xs={12} justifyContent="flex-start">
              {props.changePasswordSuccess !== true && (
                <Button
                  disabled={(() => {
                    if (type === "changepassword") {
                      return (
                        !values.oldpassword ||
                        !values.password ||
                        !values.confirm ||
                        errors.oldpassword ||
                        errors.password ||
                        errors.confirm
                      );
                    }
                    if (type === "resetpassword") {
                      return (
                        isCodeExpired ||
                        !values.code ||
                        !values.password ||
                        !values.confirm ||
                        errors.code ||
                        errors.password ||
                        errors.confirm
                      );
                    }
                    if (type === "changepasswordfirstime") {
                      return (
                        !values.password ||
                        !values.confirm ||
                        errors.password ||
                        errors.confirm
                      );
                    }
                    return true;
                  })()}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  className={formClasses.submitButton}
                >
                  {type === 'changepassword' ? 'Change Password' : 'Reset Password'}
                </Button>
              )}
            </Grid>
          </Box>

          <Box mt={2} textAlign="center">
            {type === "resetpassword" && (
              <Grid item container xs={12} justifyContent="center" spacing={2}>
                <Grid item>
                  <Button
                    onClick={() => props.handleChangeLocation("login")}
                    className={formClasses.backButton}
                  >
                    Back to Login
                  </Button>
                </Grid>
                {props.changePasswordSuccess !== true && (
                  <Grid item>
                    <Typography variant="body2" component="span" color="textSecondary">
                      |
                    </Typography>
                  </Grid>
                )}
                {props.changePasswordSuccess !== true && (
                  <Grid item style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      onClick={() => {
                        setResendLoading(true);
                        props.forgotPass(props.forgotPassEmail);
                      }}
                      disabled={resendCooldown > 0 || resendLoading}
                      className={formClasses.backButton}
                    >
                      Resend Code
                    </Button>
                    {resendCooldown > 0 && (
                      <Typography variant="body2" component="span" color="textSecondary" style={{ marginLeft: 4 }}>
                        in {formatCooldown(resendCooldown)}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </form>
        );
      }}
    </Formik>
     </Grid>
   </Grid>
  );
};

export default ResetPasswordFormikComponent;
